// Каталог колонок официального контура — то единственное, чем контур отличается от табеля в
// общей сетке.
//
// ## Четыре денежные величины стоят рядом, и это не избыточность
//
// «К выдаче», «выплачено», «заведено» и «остаток» показываются все четыре и всегда. Убрать
// «заведено» — и человек увидит экран, где деньги пропали: транш заведён, а остаток тот же.
// Показать только «заведено» — и он выдаст те же деньги второй раз. Тождество, на котором
// держится стык с ведомостью: к выдаче = выплачено + остаток (Шатун А.: 50 000 + 12 764 =
// 62 764), и запланированное в него не входит.
//
// ## Отрицательный остаток — переплата, а не ошибка
//
// У заказчика за период 15.06 — 15.07 таких тринадцать человек на 15 127,11 ₽. Ноль вместо
// минуса скрыл бы факт, из-за которого эти деньги не переносятся в следующий период.
//
// ## Все четыре поведения ячейки встречаются здесь (правило 6)
//
// Полная сумма и процент вводятся, удержание и остаток считаются движком, транши и состояние
// читаются из другой сущности и ведут туда, где правятся, закрытый период блокирован с
// причиной.

import type { CellState, GridColumn } from '@/shared/grid';
import type { OfficialRowOut, StateOptionOut, TrancheOut } from '@/shared/api/endpoints/official';
import { formatKopecks } from '@/shared/money/format';
import { moneyErrorText, parseRublesToKopecks } from '@/shared/money/parse';

export const COLUMN_GROSS = 'gross';
export const COLUMN_PERCENT = 'percent';
export const COLUMN_NOTE = 'note';

/**
 * Процент удержания человеку: «70 %», «12,5 %».
 *
 * Приходит строкой («70.00») и строкой же показывается: разбор через число превратил бы его в
 * приближение с плавающей точкой, а на этом проценте решает половина копейки.
 */
export function formatPercent(value: string | null): string {
  if (value === null || value.trim() === '') {
    return '—';
  }
  const [целая, дробная = ''] = value.trim().replace(',', '.').split('.');
  const хвост = дробная.replace(/0+$/, '');
  return хвост === '' ? `${целая} %` : `${целая},${хвост} %`;
}

/** Сумма траншей одного вида. Обратные записи входят со своим минусом: это и есть «с учётом сторно». */
export function sumTranches(
  tranches: readonly TrancheOut[],
  kind: string,
  weekNo?: number,
): TrancheSum {
  const свои = tranches.filter(
    (транш) => транш.kind === kind && (weekNo === undefined || транш.week_no === weekNo),
  );
  return {
    total: свои.reduce((итог, транш) => итог + транш.amount, 0),
    count: свои.length,
    paid: свои.reduce((итог, транш) => итог + (транш.is_paid ? транш.amount : 0), 0),
    hasUnpaid: свои.some((транш) => !транш.is_paid),
    // Сторно отдельным числом: сложенное с исходной выплатой оно даёт в ячейке молчаливый ноль,
    // и «выплаты не было» становится неотличимо от «выплата была и её сняли».
    reversed: свои
      .filter((транш) => транш.reversal_of_id !== null)
      .reduce((итог, транш) => итог + транш.amount, 0),
  };
}

export interface TrancheSum {
  readonly total: number;
  readonly count: number;
  readonly paid: number;
  readonly hasUnpaid: boolean;
  /** Сумма обратных записей, отрицательная. Ноль — сторно не делали. */
  readonly reversed: number;
}

export interface ColumnsOptions {
  /** Номера недельных колонок из настройки: в период с 15 по 15 попадает до пяти недель. */
  readonly weeks: readonly number[];
  readonly states: readonly StateOptionOut[];
  readonly canEdit: boolean;
  /** Почему править нельзя. Обязательно, когда `canEdit` ложно: молчание читается как поломка. */
  readonly blockedReason: string | null;
  /** Показывать колонку переноса: до закрытия периода она пуста у всех и только ест ширину. */
  readonly showCarried: boolean;
  /** Открыть панель строки: транши, исполнительные документы, отказ от переноса. */
  readonly onOpenRow: (row: OfficialRowOut) => void;
  /** Сменить состояние строки прямо в ячейке. Сценарий 72. */
  readonly onChangeState: (row: OfficialRowOut, state: string) => void;
  /**
   * Показан ли не весь период: включён отбор по состоянию либо поиск по фамилии.
   *
   * Подвал складывает ТО, ЧТО ВИДНО, а полоса итогов наверху — весь период. Пока это не
   * названо словом, два числа под одной шапкой расходятся и читаются как ошибка расчёта.
   */
  readonly filtered: boolean;
}

export function buildColumns({
  weeks,
  states,
  canEdit,
  blockedReason,
  showCarried,
  onOpenRow,
  onChangeState,
  filtered,
}: ColumnsOptions): GridColumn<OfficialRowOut>[] {
  const причинаЗапрета =
    blockedReason ?? 'Вести официальный контур может финансовый отдел или администратор.';

  /** Итог колонки по ПОКАЗАННЫМ строкам. Тз, раздел 5.4.5: итоги по каждой денежной колонке. */
  const подвал = (
    выбрать: (строка: OfficialRowOut) => number,
    className?: string,
  ): ((строки: readonly OfficialRowOut[]) => CellState) => {
    return (строки) => ({
      text: formatKopecks(строки.reduce((сумма, строка) => сумма + выбрать(строка), 0)),
      behaviour: 'computed',
      className,
    });
  };

  /** Денежная ячейка «только читать»: считается движком и объясняет, из чего сложилась. */
  const считается = (значение: number, пояснение: string, className?: string): CellState => ({
    text: formatKopecks(значение),
    behaviour: 'computed',
    reason: пояснение,
    className,
  });

  const фамилия: GridColumn<OfficialRowOut> = {
    id: 'employee',
    title: 'Фамилия и имя',
    width: 210,
    sticky: true,
    align: 'left',
    cell: (строка): CellState => ({
      text: строка.employee_name,
      behaviour: 'linked',
      reason:
        (строка.is_carried_in
          ? `Строка перенесена из периода ${строка.carried_from_title ?? 'прошлого периода'}. `
          : '') +
        'Транши, исполнительные документы и отказ от переноса — в панели строки. Сама фамилия ' +
        'правится в карточке человека.',
      render: (
        <button
          type='button'
          onClick={(событие) => {
            событие.stopPropagation();
            onOpenRow(строка);
          }}
          className='flex w-full items-center gap-1 text-left'
        >
          <span className='min-w-0 truncate text-text-strong-950 underline decoration-stroke-soft-200 underline-offset-2'>
            {строка.employee_name}
          </span>
          {строка.is_carried_in && (
            // Перенесённая строка обязана быть видна в самой сетке: это не тот же долг, что
            // начислен в этом периоде, и процент по исполнительному документу к ней не
            // применяется второй раз.
            //
            // Пометка стоит В ОДНУ строку с фамилией, а из какого периода перенос — в пояснении
            // ячейки. Второй строкой она распирала строку сетки до 33 точек вместо 28 (замер
            // безголовым браузером 18.08.2026), а виртуализация раскладывает строки по 28: на
            // тринадцати перенесённых строках расхождение накапливается, и внизу ведомости
            // каретка встаёт не на ту строку, которую видит человек.
            <span className='shrink-0 text-grid-sm text-text-sub-600'>перенос</span>
          )}
        </button>
      ),
    }),
    // Подпись подвала называет, ЧТО сложено. При включённом отборе итог по показанным строкам
    // не равен итогу периода наверху, и без этой подписи расхождение читается как ошибка счёта.
    footer: (): CellState => ({
      text: filtered ? 'Итого по показанным строкам' : 'Итого по периоду',
      behaviour: 'computed',
    }),
  };

  const полная: GridColumn<OfficialRowOut> = {
    id: COLUMN_GROSS,
    title: 'Полная официальная',
    subtitle: 'до удержания',
    width: 140,
    align: 'right',
    cell: (строка): CellState => {
      if (!canEdit) {
        return {
          text: formatKopecks(строка.gross_official),
          behaviour: 'blocked',
          reason: причинаЗапрета,
        };
      }
      return {
        text: formatKopecks(строка.gross_official),
        behaviour: 'input',
        raw: рублямиДляПравки(строка.gross_official),
      };
    },
    footer: подвал((строка) => строка.gross_official),
  };

  const процент: GridColumn<OfficialRowOut> = {
    id: COLUMN_PERCENT,
    title: 'Процент',
    subtitle: 'исп. документ',
    width: 104,
    align: 'right',
    cell: (строка): CellState => {
      // Перенесённая строка идёт без процента намеренно: удержание уже применено в том периоде,
      // где деньги начислялись, и применить его второй раз значит отнять их дважды.
      if (строка.is_carried_in) {
        return {
          text: '—',
          behaviour: 'blocked',
          reason:
            'У перенесённой строки процента нет: удержание уже применили в том периоде, где ' +
            'деньги начислялись. Применить его второй раз значит отнять их дважды.',
        };
      }
      if (!canEdit) {
        return {
          text: formatPercent(строка.writ_percent),
          behaviour: 'blocked',
          reason: причинаЗапрета,
        };
      }
      return {
        text: formatPercent(строка.writ_percent),
        behaviour: 'input',
        raw: строка.writ_percent ?? '',
      };
    },
  };

  const удержано: GridColumn<OfficialRowOut> = {
    id: 'writ',
    title: 'Удержано',
    subtitle: 'по документу',
    width: 130,
    align: 'right',
    cell: (строка): CellState =>
      строка.writ_percent === null
        ? // Ноль по расчёту, а не «нет данных»: движок отработал и получил ноль (правило 7).
          считается(
            строка.writ_amount,
            'Удержания нет: процент по исполнительному документу не задан. Это посчитанный ноль, а не пустая ячейка.',
            'bg-bg-weak-50',
          )
        : считается(
            строка.writ_amount,
            `Считается движком: ${formatKopecks(строка.gross_official)} × ${formatPercent(строка.writ_percent)}, округление вверх с половины копейки.`,
          ),
    footer: подвал((строка) => строка.writ_amount),
  };

  const кВыдаче: GridColumn<OfficialRowOut> = {
    id: 'payable',
    title: 'К выдаче',
    subtitle: 'официально',
    width: 130,
    align: 'right',
    cell: (строка): CellState =>
      считается(
        строка.payable,
        'Полная официальная сумма минус удержание по исполнительному документу. Это и есть «Всего к выдаче официально».',
        'font-medium',
      ),
    footer: подвал((строка) => строка.payable, 'font-medium'),
  };

  const основная: GridColumn<OfficialRowOut> = {
    id: 'main',
    title: 'Основная выплата',
    subtitle: '15-30 числа',
    width: 140,
    align: 'right',
    cell: (строка): CellState => траншеваяЯчейка(строка, 'main'),
    footer: подвал((строка) => sumTranches(строка.tranches, 'main').total),
  };

  const недельные: GridColumn<OfficialRowOut>[] = weeks.map((номер) => ({
    id: `week-${номер}`,
    title: `Неделя ${номер}`,
    width: 108,
    align: 'right',
    cell: (строка): CellState => траншеваяЯчейка(строка, 'weekly', номер),
    footer: подвал((строка) => sumTranches(строка.tranches, 'weekly', номер).total),
  }));

  const выплачено: GridColumn<OfficialRowOut> = {
    id: 'paid',
    title: 'Выплачено',
    subtitle: 'с датой',
    width: 130,
    align: 'right',
    cell: (строка): CellState =>
      считается(
        строка.paid,
        'Сумма траншей с проставленной датой выплаты. Заведённые без даты сюда не входят.',
      ),
    footer: подвал((строка) => строка.paid),
  };

  const заведено: GridColumn<OfficialRowOut> = {
    id: 'planned',
    title: 'Заведено',
    subtitle: 'ещё не выдано',
    width: 130,
    align: 'right',
    cell: (строка): CellState =>
      считается(
        строка.planned,
        'Заведено траншами, но не выплачено. Остаток эта сумма НЕ уменьшает: пока деньги не выданы, человеку должны столько же.',
        строка.planned === 0 ? undefined : 'text-text-sub-600',
      ),
    footer: подвал((строка) => строка.planned),
  };

  const перенесено: GridColumn<OfficialRowOut> = {
    id: 'carried',
    title: 'Перенесено',
    subtitle: 'в следующий',
    width: 130,
    align: 'right',
    cell: (строка): CellState =>
      считается(
        строка.carried_out,
        'Ушло переносом в следующий период при закрытии. Остаток эта сумма гасит, но выплатой не является.',
      ),
    footer: подвал((строка) => строка.carried_out),
  };

  const остаток: GridColumn<OfficialRowOut> = {
    id: 'balance',
    title: 'Остаток',
    subtitle: 'к выдаче',
    width: 140,
    align: 'right',
    cell: (строка): CellState => {
      if (строка.balance < 0) {
        return {
          text: formatKopecks(строка.balance),
          behaviour: 'computed',
          reason:
            'Отрицательный остаток — это переплата: выдали больше, чем начислено. Переносом она ' +
            'не является, разбирается вручную.',
          className: 'font-medium text-warning-base',
        };
      }
      const запланировано =
        строка.planned > 0
          ? ` Из них ${formatKopecks(строка.planned)} уже заведено траншем, но не выплачено.`
          : '';
      return {
        text: formatKopecks(строка.balance),
        behaviour: 'computed',
        reason: `К выдаче минус выплаченное минус перенесённое.${запланировано}`,
        className: 'font-medium',
      };
    },
    // Остаток складывается СО ЗНАКОМ: переплаты в периоде отрицательные, и сумма без них
    // завысила бы непогашенное. Полоса итогов наверху те же деньги разводит на две величины —
    // «непогашено» и «переплата», — и подвал обязан сходиться с их разностью.
    footer: подвал((строка) => строка.balance, 'font-medium'),
  };

  const состояние: GridColumn<OfficialRowOut> = {
    id: 'state',
    title: 'Состояние',
    subtitle: 'справочник',
    width: 200,
    align: 'left',
    cell: (строка): CellState => ({
      text: строка.state_title,
      behaviour: canEdit ? 'linked' : 'blocked',
      reason: canEdit
        ? 'Состояние берётся из справочника. У заказчика это цвет заливки ячейки: не отбирается, ' +
          'не суммируется и теряется при копировании — здесь по нему отбирается список.'
        : причинаЗапрета,
      render: canEdit ? (
        <select
          value={строка.state}
          aria-label={`Состояние строки, ${строка.employee_name}`}
          onClick={(событие) => событие.stopPropagation()}
          // Сетка перехватывает стрелки для хождения по ячейкам. Без этого выбрать состояние
          // с клавиатуры было бы нельзя вовсе: стрелка уводила бы каретку вместо смены пункта.
          onKeyDown={(событие) => событие.stopPropagation()}
          onChange={(событие) => onChangeState(строка, событие.target.value)}
          className='w-full truncate bg-transparent text-grid text-text-strong-950 outline-none'
        >
          {states.map((пункт) => (
            <option key={пункт.code} value={пункт.code}>
              {пункт.title}
            </option>
          ))}
        </select>
      ) : undefined,
    }),
  };

  const заметка: GridColumn<OfficialRowOut> = {
    id: COLUMN_NOTE,
    title: 'Заметка',
    width: 220,
    align: 'left',
    cell: (строка): CellState => {
      // Отказ от переноса живёт основанием, а не заметкой: это денежное решение, и оно обязано
      // быть видно прямо в строке, а не только в журнале.
      if (строка.carry_refused_basis !== null) {
        return {
          text: `Перенос отменён: ${строка.carry_refused_basis}`,
          behaviour: 'blocked',
          reason:
            'Основание отказа от переноса правкой не меняется. Нужен другой текст — снимите ' +
            'отказ в панели строки и оформите заново.',
          className: 'text-warning-base',
        };
      }
      if (!canEdit) {
        return {
          text: строка.note ?? '',
          behaviour: 'blocked',
          reason: причинаЗапрета,
        };
      }
      return {
        text: строка.note ?? '',
        behaviour: 'input',
        raw: строка.note ?? '',
      };
    },
  };

  return [
    фамилия,
    полная,
    процент,
    удержано,
    кВыдаче,
    основная,
    ...недельные,
    выплачено,
    заведено,
    ...(showCarried ? [перенесено] : []),
    остаток,
    состояние,
    заметка,
  ];
}

/**
 * Ячейка транша: сумма и, отдельно, выдана она или только заведена.
 *
 * Заведённый транш показывается бледным — тем же языком, что план в табеле. Иначе выданное и
 * обещанное выглядели бы одинаково, а это разные деньги.
 */
function траншеваяЯчейка(строка: OfficialRowOut, kind: string, weekNo?: number): CellState {
  const итог = sumTranches(строка.tranches, kind, weekNo);
  if (итог.count === 0) {
    // Пусто, а не ноль: транша нет вовсе, и это другой факт, чем «транш на ноль».
    return {
      text: '',
      behaviour: 'linked',
      reason: 'Транша нет. Завести его можно в панели строки — нажмите фамилию.',
    };
  }
  if (итог.reversed !== 0) {
    // Сторнированное остаётся видимым, а не превращается в ноль (правило 8). Само число —
    // действующая сумма, но ячейка говорит, из чего она получилась.
    return {
      text: formatKopecks(итог.total),
      behaviour: 'linked',
      reason:
        `Заведено ${formatKopecks(итог.total - итог.reversed)}, из них сторнировано ` +
        `${formatKopecks(-итог.reversed)}. Разбор — в панели строки, переключателем ` +
        '«с учётом сторно».',
      className: итог.total === 0 ? 'text-text-sub-600 line-through' : 'text-warning-base',
    };
  }
  return {
    text: formatKopecks(итог.total),
    behaviour: 'linked',
    reason: итог.hasUnpaid
      ? 'Транш заведён, но дата выплаты не проставлена: остаток он не уменьшает. Провести его — в панели строки.'
      : `Выплачено ${formatKopecks(итог.paid)}. Правки денежной записи не существует: неверную сумму снимают сторно в панели строки.`,
    className: итог.hasUnpaid ? 'text-text-sub-600' : undefined,
  };
}

/** Копейки в то, что человек будет править: «25934.00» правится хуже, чем «25 934». */
function рублямиДляПравки(копейки: number): string {
  const рубли = Math.trunc(Math.abs(копейки) / 100);
  const остаток = Math.abs(копейки) % 100;
  const знак = копейки < 0 ? '-' : '';
  return остаток === 0 ? `${знак}${рубли}` : `${знак}${рубли},${String(остаток).padStart(2, '0')}`;
}

/**
 * Проверка значения по колонке. Одна на ввод с клавиатуры и на вставку из буфера: иначе
 * вставленное проверялось бы иначе, чем набранное, и в деньги попадал бы мусор.
 */
export function validateCell(значение: string, _cell: unknown, columnId: string): string | null {
  if (columnId === COLUMN_GROSS) {
    if (значение === '') {
      return 'Полная официальная сумма пустой не бывает: если человеку в этом периоде ничего не положено, строку не заводят.';
    }
    const разбор = parseRublesToKopecks(значение);
    return разбор.ok ? null : moneyErrorText(разбор.reason, 'полную официальную сумму');
  }
  if (columnId === COLUMN_PERCENT) {
    if (значение === '') {
      return null; // Пусто снимает удержание — это законное действие, а не ошибка ввода.
    }
    // Именно разбором текста, а не через Number: тот пропускает «0x10» и «1e1», и они падают
    // уже на сервере, то есть после того, как человек решил, что ввод принят.
    const текст = значение.replace(',', '.');
    if (!/^\d{1,3}(\.\d{1,2})?$/.test(текст)) {
      return 'Процент пишется числом: 25, 50, 70. Знак процента добавлять не нужно.';
    }
    const число = Number(текст);
    if (число < 0 || число > 100) {
      return 'Процент удержания бывает от 0 до 100.';
    }
    return null;
  }
  if (columnId === COLUMN_NOTE) {
    return значение.length > 2000 ? 'Заметка длиннее двух тысяч знаков не сохранится.' : null;
  }
  return null;
}
